<!-- Bug Report Button - only shown when feature is enabled -->
<ng-container *ngIf="isEnabled">
  <div class="bug-report-button-container">
    <!-- Bug report button -->
    <button
      class="btn btn-action-outline btn-bug"
      (click)="openBugReport()"
      [disabled]="isCapturing"
      [title]="'BugReport.reportBug' | translate"
    >
      <i class="bi bi-bug" *ngIf="!isCapturing"></i>
      <span class="spinner-border spinner-border-sm" *ngIf="isCapturing"></span>
    </button>

    <!-- Recording toggle -->
    <button
      class="btn btn-action-outline btn-record"
      (click)="toggleRecording()"
      [class.active]="isRecording"
      [class.btn-recording]="isRecording"
      [title]="(isRecording ? 'BugReport.stopRecording' : 'BugReport.startRecording') | translate"
    >
      <i class="bi" [ngClass]="isRecording ? 'bi-stop-circle-fill text-danger recording-pulse' : 'bi-record-circle'"></i>
    </button>
  </div>
</ng-container>

<!-- Bug Report Modal -->
<ng-template #bugReportModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-bug me-2"></i>
      {{ 'BugReport.title' | translate }}
    </h5>
    <button type="button" class="btn-close" (click)="closeModal()"></button>
  </div>

  <div class="modal-body bug-report-modal-body">
    <!-- Screenshot / Images Section -->
    <div class="screenshot-section mb-3">
      <!-- Annotator canvas for the selected image (main drawing area) -->
      <div class="screenshot-preview-container mb-2">
        <app-screenshot-annotator
          *ngIf="selectedImageBase64"
          [screenshotBase64]="selectedImageBase64"
          (annotatedImage)="onAnnotatedImage($event)"
        ></app-screenshot-annotator>
      </div>

      <!-- Screenshot actions -->
      <div class="screenshot-actions mb-2">
        <button class="btn btn-sm btn-outline-secondary" (click)="retakeScreenshot()">
          <i class="bi bi-arrow-clockwise me-1"></i>
          {{ 'BugReport.retakeScreenshot' | translate }}
        </button>
        <button
          class="btn btn-sm btn-outline-primary ms-2"
          (click)="analyzeWithAI()"
          [disabled]="isAnalyzing || images.length === 0"
        >
          <span class="spinner-border spinner-border-sm me-1" *ngIf="isAnalyzing"></span>
          <i class="bi bi-robot me-1" *ngIf="!isAnalyzing"></i>
          {{ 'BugReport.analyzeWithAI' | translate }}
        </button>
      </div>

      <!-- Image strip: thumbnails + add -->
      <div class="image-strip">
        <label class="form-label fw-bold small mb-1">
          <i class="bi bi-images me-1"></i>
          {{ 'BugReport.screenshot' | translate }}
          <span class="text-muted fw-normal">({{ images.length }}/{{ MAX_IMAGES }})</span>
        </label>
        <div class="image-thumbnail-row">
          <div
            *ngFor="let img of images; let i = index"
            class="image-thumbnail-wrapper"
            [class.selected]="i === selectedImageIndex"
            (click)="selectImage(i)"
          >
            <img [src]="img.previewUrl" class="image-thumbnail" [alt]="img.fileName" />
            <span class="thumbnail-type-badge" [ngClass]="{
              'bg-primary': img.attachmentType === 'screenshot',
              'badge-soft-success': img.attachmentType === 'paste',
              'badge-soft-info': img.attachmentType === 'upload'
            }">
              <i class="bi" [ngClass]="{
                'bi-camera': img.attachmentType === 'screenshot',
                'bi-clipboard': img.attachmentType === 'paste',
                'bi-upload': img.attachmentType === 'upload'
              }"></i>
            </span>
            <button
              class="btn-remove-image"
              (click)="removeImage(i); $event.stopPropagation()"
              [attr.aria-label]="'General.delete' | translate"
              *ngIf="images.length > 1"
            >
              <i class="bi bi-x"></i>
            </button>
            <span class="thumbnail-annotated-indicator" *ngIf="img.annotatedBase64">
              <i class="bi bi-pencil-fill"></i>
            </span>
          </div>

          <!-- Add image button (inline in thumbnail row) -->
          <button
            class="btn btn-sm btn-outline-secondary image-add-button"
            *ngIf="images.length < MAX_IMAGES"
            (click)="fileInput.click()"
          >
            <i class="bi bi-plus-lg me-1"></i>
            {{ 'BugReport.addImage' | translate }}
          </button>
        </div>

        <!-- Paste hint -->
        <div class="paste-hint text-muted small mt-1" *ngIf="images.length < MAX_IMAGES">
          <i class="bi bi-clipboard me-1"></i>
          {{ 'BugReport.pasteHint' | translate }}
        </div>
      </div>

      <!-- Hidden file input -->
      <input
        type="file"
        #fileInput
        accept="image/*"
        multiple
        class="d-none"
        (change)="onFileInputChange($event)"
      />
    </div>

    <!-- AI Analysis Result -->
    <div *ngIf="aiAnalysis" class="ai-analysis-section mb-3">
      <div class="alert alert-info">
        <h6 class="alert-heading">
          <i class="bi bi-robot me-1"></i>
          {{ 'BugReport.aiAnalysisResult' | translate }}
        </h6>
        <p class="mb-2" *ngIf="aiAnalysis.aiAnalysis">{{ aiAnalysis.aiAnalysis }}</p>
        <div *ngIf="aiAnalysis.detectedModule" class="mb-1">
          <strong>{{ 'BugReport.detectedModule' | translate }}:</strong> {{ aiAnalysis.detectedModule }}
        </div>
        <div *ngIf="aiAnalysis.suggestedSeverity" class="mb-1">
          <strong>{{ 'BugReport.suggestedSeverity' | translate }}:</strong>
          <span class="badge ms-1" [ngClass]="{
            'badge-soft-success': aiAnalysis.suggestedSeverity === 'low',
            'badge-soft-warning': aiAnalysis.suggestedSeverity === 'medium',
            'badge-soft-dark': aiAnalysis.suggestedSeverity === 'high',
            'badge-soft-danger': aiAnalysis.suggestedSeverity === 'critical'
          }">{{ aiAnalysis.suggestedSeverity }}</span>
        </div>
        <!-- Claude Code Prompt -->
        <div *ngIf="aiAnalysis.claudeCodePrompt" class="mt-2">
          <button class="btn btn-sm btn-outline-dark" (click)="copyClaudePrompt()">
            <i class="bi bi-clipboard me-1"></i>
            {{ 'BugReport.copyClaudePrompt' | translate }}
          </button>
        </div>
      </div>
    </div>

    <!-- Title -->
    <div class="mb-3">
      <label class="form-label fw-bold" for="bugTitle">
        {{ 'BugReport.bugTitle' | translate }} <span class="text-danger">*</span>
      </label>
      <input
        type="text"
        id="bugTitle"
        class="form-control"
        [(ngModel)]="title"
        [placeholder]="'BugReport.bugTitlePlaceholder' | translate"
        maxlength="200"
      />
    </div>

    <!-- Description -->
    <div class="mb-3">
      <label class="form-label fw-bold" for="bugDescription">
        {{ 'BugReport.bugDescription' | translate }} <span class="text-danger">*</span>
      </label>
      <textarea
        id="bugDescription"
        class="form-control"
        [(ngModel)]="description"
        [placeholder]="'BugReport.bugDescriptionPlaceholder' | translate"
        rows="4"
        maxlength="4000"
      ></textarea>
    </div>

    <!-- Severity -->
    <div class="mb-3">
      <label class="form-label fw-bold">{{ 'BugReport.severity' | translate }}</label>
      <div class="btn-group w-100" role="group">
        <input type="radio" class="btn-check" name="severity" id="sev-low" value="low" [(ngModel)]="severity" />
        <label class="btn btn-outline-success" for="sev-low">{{ 'BugReport.severityLow' | translate }}</label>

        <input type="radio" class="btn-check" name="severity" id="sev-medium" value="medium" [(ngModel)]="severity" />
        <label class="btn btn-outline-warning" for="sev-medium">{{ 'BugReport.severityMedium' | translate }}</label>

        <input type="radio" class="btn-check" name="severity" id="sev-high" value="high" [(ngModel)]="severity" />
        <label class="btn btn-outline-dark" for="sev-high">{{ 'BugReport.severityHigh' | translate }}</label>

        <input type="radio" class="btn-check" name="severity" id="sev-critical" value="critical" [(ngModel)]="severity" />
        <label class="btn btn-outline-danger" for="sev-critical">{{ 'BugReport.severityCritical' | translate }}</label>
      </div>
    </div>

    <!-- Advanced section (collapsible) -->
    <div class="advanced-section">
      <button class="btn btn-sm btn-link p-0 mb-2" (click)="showAdvanced = !showAdvanced">
        <i class="bi" [ngClass]="showAdvanced ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        {{ 'BugReport.advancedInfo' | translate }}
      </button>

      <div *ngIf="showAdvanced" class="advanced-content">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">{{ 'BugReport.currentUrl' | translate }}</label>
            <input type="text" class="form-control form-control-sm" [(ngModel)]="currentUrl" readonly />
          </div>
          <div class="col-md-6">
            <label class="form-label">{{ 'BugReport.detectedModule' | translate }}</label>
            <input type="text" class="form-control form-control-sm" [(ngModel)]="moduleName" />
          </div>
        </div>

        <div class="mt-2" *ngIf="consoleErrors">
          <label class="form-label">{{ 'BugReport.consoleErrors' | translate }}</label>
          <textarea class="form-control form-control-sm font-monospace" rows="3" [(ngModel)]="consoleErrors" readonly></textarea>
        </div>

        <div class="mt-2" *ngIf="networkErrors">
          <label class="form-label">{{ 'BugReport.networkErrors' | translate }}</label>
          <textarea class="form-control form-control-sm font-monospace" rows="3" [(ngModel)]="networkErrors" readonly></textarea>
        </div>

        <div class="mt-2 text-muted small">
          <i class="bi bi-info-circle me-1"></i>
          {{ 'BugReport.browserInfoLabel' | translate }}: {{ browserInfo | slice:0:80 }}...
          <br />
          {{ 'BugReport.screenResolutionLabel' | translate }}: {{ screenResolution }}
        </div>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="closeModal()">
      {{ 'General.cancel' | translate }}
    </button>
    <button
      type="button"
      class="btn btn-primary"
      (click)="submitBugReport()"
      [disabled]="isSubmitting || !title.trim() || !description.trim()"
    >
      <span class="spinner-border spinner-border-sm me-1" *ngIf="isSubmitting"></span>
      <i class="bi bi-send me-1" *ngIf="!isSubmitting"></i>
      {{ 'BugReport.submit' | translate }}
    </button>
  </div>
</ng-template>

<!-- Success Modal -->
<ng-template #successModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">
      <i class="bi bi-check-circle me-2"></i>
      {{ 'BugReport.submitSuccess' | translate }}
    </h5>
    <button type="button" class="btn-close" (click)="closeModal()"></button>
  </div>
  <div class="modal-body">
    <p>{{ 'BugReport.submitSuccessMessage' | translate }}</p>

    <div *ngIf="submitResult?.freshdeskTicketId" class="mb-3">
      <strong>{{ 'BugReport.freshdeskTicket' | translate }}:</strong>
      <a *ngIf="submitResult?.freshdeskTicketUrl" [href]="submitResult.freshdeskTicketUrl" target="_blank" class="ms-1">
        #{{ submitResult.freshdeskTicketId }}
        <i class="bi bi-box-arrow-up-right ms-1"></i>
      </a>
      <span *ngIf="!submitResult?.freshdeskTicketUrl">#{{ submitResult?.freshdeskTicketId }}</span>
    </div>

    <div *ngIf="!submitResult?.freshdeskTicketId && submitResult?.emailFallbackSent" class="alert alert-info mb-3">
      <i class="bi bi-envelope-check me-2"></i>
      {{ 'BugReport.emailFallbackSent' | translate }}
    </div>

    <div *ngIf="!submitResult?.freshdeskTicketId && !submitResult?.emailFallbackSent" class="alert alert-warning mb-3">
      <i class="bi bi-exclamation-triangle me-2"></i>
      {{ 'BugReport.noTicketCreated' | translate }}
    </div>


  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-success" (click)="closeModal()">
      {{ 'General.close' | translate }}
    </button>
  </div>
</ng-template>
